import { useCallback, useState } from 'react';
import { CampaignType_Enum as CampaignType, Recipient } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { List, ListListsResponse } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2/service.pb';
import { UseInfiniteQueryResult } from 'react-query';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, SearchField, useModalControl } from '@frontend/design-system';
import { DeleteListModal } from '../saved-list/delete-list-modal';
import { TrayMode } from '../types';
import { NewSavedListItem } from './new-saved-list-item';
import { SavedListModalContent } from './saved-list-modal-content';
import { useDebouncedSearch } from './use-debounced-search';

type Props = {
  campaignType: CampaignType;
  contactLists: List[];
  contactListsQueryResult: UseInfiniteQueryResult<ListListsResponse>;
  createContactList: (list: List) => Promise<void>;
  deleteContactList: (listId: string) => Promise<void>;
  deselectContactList: (listId: string) => void;
  recipientsToUpload: Recipient[];
  renameContactList: (list: List) => Promise<void>;
  searchQuery: string;
  selectContactList: (listId: string) => void;
  selectedContactLists: string[];
  setMode: (mode: TrayMode) => void;
  setRecipientsToUpload: (recipients: Recipient[]) => void;
  setSearchQuery: (searchQuery: string) => void;
  trackingIdPrefix: string;
};

export const SavedListModal = ({
  campaignType,
  contactLists,
  contactListsQueryResult,
  createContactList,
  deleteContactList,
  deselectContactList,
  recipientsToUpload,
  renameContactList,
  searchQuery,
  selectContactList,
  selectedContactLists,
  setMode,
  setRecipientsToUpload,
  setSearchQuery,
  trackingIdPrefix,
}: Props) => {
  const { t } = useTranslation('bulk-messaging');

  const { searchFieldProps, isDebouncing } = useDebouncedSearch(
    searchQuery,
    setSearchQuery,
    contactListsQueryResult.isFetched
  );
  const [listIdToDelete, setListIdToDelete] = useState<string | undefined>(undefined);
  const [listIdToRename, setListIdToRename] = useState<string | undefined>(undefined);
  const { modalProps: deleteModalProps, triggerProps: deleteTriggerProps } = useModalControl();

  const handleCheck = useCallback(
    (listId?: string) => {
      if (!listId) return;
      selectedContactLists.includes(listId) ? deselectContactList(listId) : selectContactList(listId);
    },
    [deselectContactList, selectContactList, selectedContactLists]
  );

  const handleDelete = useCallback(
    (listId?: string) => {
      deleteTriggerProps.onClick();
      setListIdToDelete(listId);
    },
    [deleteTriggerProps]
  );

  return (
    <>
      <div
        css={{
          alignItems: 'center',
          columnGap: theme.spacing(1),
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          margin: theme.spacing(2, 0),
        }}
      >
        <SearchField
          {...searchFieldProps}
          aria-label={t('Search Lists')}
          data-trackingid={`${trackingIdPrefix}-contacts-list-search-input`}
          maxLength={70}
          name='search-lists'
          placeholder={t('Search Lists')}
        />
        <Button
          aria-label={t('Upload CSV List')}
          css={{ padding: theme.spacing(1) }}
          hoverLabel={t('Upload CSV List')}
          onClick={() => setMode('csv')}
          trackingId={`${trackingIdPrefix}-contacts-upload-csv-btn`}
          variant='tertiary'
        >
          <Icon name='upload' color='default' />
        </Button>
      </div>

      {recipientsToUpload.length > 0 && (
        <NewSavedListItem
          campaignType={campaignType}
          createContactList={createContactList}
          recipientsToUpload={recipientsToUpload}
          setRecipientsToUpload={setRecipientsToUpload}
          trackingIdPrefix={trackingIdPrefix}
        />
      )}

      <SavedListModalContent
        contactLists={contactLists}
        contactListsQueryResult={contactListsQueryResult}
        handleCheck={handleCheck}
        handleDelete={handleDelete}
        isDebouncing={isDebouncing}
        listIdToRename={listIdToRename}
        recipientsToUpload={recipientsToUpload}
        renameContactList={renameContactList}
        selectedContactLists={selectedContactLists}
        setListIdToRename={setListIdToRename}
        setMode={setMode}
        trackingIdPrefix={trackingIdPrefix}
      />

      <DeleteListModal
        deleteContactList={deleteContactList}
        listIdToDelete={listIdToDelete}
        modalProps={deleteModalProps}
        setListIdToDelete={setListIdToDelete}
      />
    </>
  );
};
